import AccountService from "./AccountService.js";
import { mergeSnapshots, isSyncableKey } from "./syncMerge.js";
import { sanitizeAvatar, AVATAR_STORAGE_KEY } from "../utils/AvatarBuilder.js";

/**
 * État de connexion + synchronisation de la sauvegarde.
 *
 * Modèle : pendant une partie on n'écrit QUE dans localStorage. À la fin d'une
 * partie, les jeux appellent `AccountSync.push()` immédiatement (pas de debounce :
 * le lien "classement" affiché juste après la fin de partie est cliquable avant
 * qu'un push différé n'ait eu lieu, ce qui laissait le classement obsolète tant
 * qu'on ne repassait pas par la home). Pour les sauvegardes fréquentes en cours
 * de partie (ex: progression lettre par lettre de Country Nerd, pays découverts
 * sur la carte), `AccountSync.scheduleSync()` reste utilisé pour coalescer les
 * appels rapprochés. Au chargement, `init()` récupère la session puis fusionne
 * serveur↔local.
 */
const PUSH_DEBOUNCE_MS = 1500;
/** Miroir du `maxItems` du schéma de DELETE /sync (server/src/routes/sync.js). */
const DELETE_CHUNK_SIZE = 500;

export default class AccountSync {
    static user = null;
    static _pushTimer = null;
    static _initPromise = null;

    static isLoggedIn() {
        return !!this.user;
    }

    /** Récupère la session courante et fusionne les sauvegardes. Idempotent. */
    static init() {
        if (this._initPromise) return this._initPromise;
        this._initPromise = (async () => {
            const { ok, data } = await AccountService.me();
            if (!ok || !data?.user) return; // anonyme ou API indisponible
            this.user = data.user;
            await this.pull();
            await this.push(); // remonte les données locales que le serveur n'a pas
            await this.syncAvatar();
        })();
        return this._initPromise;
    }

    /** Lit les clés synchronisables de localStorage → objet plat. */
    static snapshot() {
        const snap = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (isSyncableKey(key)) snap[key] = localStorage.getItem(key);
        }
        return snap;
    }

    /** Écrit la snapshot fusionnée dans localStorage (sans supprimer d'autres clés). */
    static applyMerged(merged) {
        for (const [key, value] of Object.entries(merged)) {
            if (isSyncableKey(key)) localStorage.setItem(key, value);
        }
    }

    /** Tire la sauvegarde serveur et la fusionne dans localStorage. */
    static async pull() {
        const { ok, data } = await AccountService.getSync();
        if (!ok) return;
        this.applyMerged(mergeSnapshots(this.snapshot(), data?.data || {}));
    }

    /** Pousse immédiatement la snapshot locale et applique le retour fusionné. */
    static async push() {
        if (!this.user) return;
        const { ok, data } = await AccountService.putSync(this.snapshot());
        if (ok && data?.data) this.applyMerged(data.data);
    }

    /** Programme un push différé (coalesce plusieurs fins de partie rapprochées). */
    static scheduleSync() {
        if (!this.user) return;
        clearTimeout(this._pushTimer);
        this._pushTimer = setTimeout(() => this.push(), PUSH_DEBOUNCE_MS);
    }

    /**
     * Supprime des clés (ou toute la sauvegarde si `keys` est omis) côté
     * serveur. Le merge de `push()` ne peut qu'accroître les valeurs (max/union),
     * donc un reset local doit passer par ce endpoint dédié, pas par push().
     * No-op si anonyme ou si l'API est injoignable.
     */
    static async deleteRemote(keys) {
        if (!this.user) return;
        if (!keys) {
            const { ok, data } = await AccountService.deleteSync();
            if (ok && data?.data) this.applyMerged(data.data);
            return;
        }
        // Le schéma du endpoint plafonne à 500 clés par appel : on découpe, sinon
        // un reset d'un mode Daily (une clé par jour joué) finirait par être
        // rejeté en 400 et le serveur restaurerait les données au prochain pull.
        for (let i = 0; i < keys.length; i += DELETE_CHUNK_SIZE) {
            const { ok, data } = await AccountService.deleteSync(keys.slice(i, i + DELETE_CHUNK_SIZE));
            if (ok && data?.data) this.applyMerged(data.data);
        }
    }

    /** Appelé après login/register réussi : mémorise l'utilisateur et fusionne. */
    static async afterAuth(user) {
        this.user = user;
        await this.pull();
        await this.push();
        await this.syncAvatar();
    }

    /**
     * Réconcilie l'avatar serveur/local. Contrairement au reste de la sauvegarde
     * (merge commutatif max/union), l'avatar est un réglage de profil : le serveur
     * gagne s'il en a un (autre appareil) ; sinon on remonte le choix local (ex :
     * personnalisé avant de créer un compte).
     */
    static async syncAvatar() {
        if (!this.user) return;
        if (this.user.avatar) {
            localStorage.setItem(AVATAR_STORAGE_KEY, this.user.avatar);
            return;
        }
        const local = localStorage.getItem(AVATAR_STORAGE_KEY);
        if (!local) return;
        let parsed;
        try {
            parsed = JSON.parse(local);
        } catch {
            return;
        }
        const { ok, data } = await AccountService.patchAvatar(sanitizeAvatar(parsed));
        if (ok && data?.avatar) this.user.avatar = JSON.stringify(data.avatar);
    }

    static clear() {
        this.user = null;
    }
}
